/**
 * UnpinPastReturnsDialog — 「ツール」→「ピンを外す（過去返却分）」（2026-10-26・booking-platform/md/customer-chat.md §13-6）
 *
 * 対象（lib/boardTools findPastReturnPins）を一覧にし、「これらのピンを外しますか？」と聞く。外したくないものはチェックを外す。
 * 「はい」で残ったものだけ外し、外した件数と失敗したもの（名前）を出す。実際に外す処理は親（LineChatBoard）が渡す。
 */
import React, { useEffect, useState } from 'react';
import { Box, Button, Checkbox, Dialog, DialogContent, DialogTitle, Typography } from '@mui/material';
import type { PastReturnPin } from '../lib/boardTools';

interface Props {
  open: boolean;
  targets: PastReturnPin[];
  onClose: () => void;
  /** 選んだ対象のピンを外す。失敗したものの key を返す */
  onUnpin: (targets: PastReturnPin[]) => Promise<string[]>;
}

const fmt = (d: string) => {
  const m = d.match(/^\d{4}-(\d{2})-(\d{2})$/);
  return m ? `${Number(m[1])}/${Number(m[2])}` : d;
};

const UnpinPastReturnsDialog: React.FC<Props> = ({ open, targets, onClose, onUnpin }) => {
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<{ done: number; failed: PastReturnPin[] } | null>(null);

  useEffect(() => {
    if (!open) return;
    setChecked(new Set(targets.map((t) => t.key)));
    setResult(null);
    setRunning(false);
  }, [open, targets]);

  const toggle = (key: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleYes = async () => {
    const chosen = targets.filter((t) => checked.has(t.key));
    if (chosen.length === 0) return;
    setRunning(true);
    const failedKeys = await onUnpin(chosen);
    setRunning(false);
    const failed = chosen.filter((t) => failedKeys.includes(t.key));
    setResult({ done: chosen.length - failed.length, failed });
  };

  const count = targets.filter((t) => checked.has(t.key)).length;

  return (
    <Dialog open={open} onClose={running ? undefined : onClose} PaperProps={{ sx: { borderRadius: '16px', p: 1, minWidth: { sm: 420 } } }}>
      <DialogTitle sx={{ fontWeight: 800, fontSize: '16px', pb: 0 }}>ピンを外す（過去返却分）</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        {result ? (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#166534' }}>{result.done} 件のピンを外しました。</Typography>
            {result.failed.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#b91c1c' }}>
                  {result.failed.length} 件は外せませんでした（もう一度お試しください）:
                </Typography>
                <ul style={{ margin: '4px 0 0', paddingLeft: 20, fontSize: 13 }} data-testid="unpin-failed">
                  {result.failed.map((t) => <li key={t.key}>{t.name}</li>)}
                </ul>
              </Box>
            )}
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button variant="contained" size="small" onClick={onClose} sx={{ fontWeight: 700 }}>閉じる</Button>
            </Box>
          </Box>
        ) : targets.length === 0 ? (
          <Box>
            <Typography variant="body2" color="text.secondary">返却日が昨日以前のピン留めはありません。</Typography>
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button size="small" onClick={onClose} sx={{ fontWeight: 700 }}>閉じる</Button>
            </Box>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>これらのピンを外しますか？</Typography>
            <Typography variant="caption" color="text.secondary">
              返却日が昨日以前のピン留め（LINE と予約チャット）です。外したくないものはチェックを外してください。
            </Typography>
            <Box sx={{ maxHeight: 360, overflowY: 'auto', mt: 1, border: '1px solid #e2e8f0', borderRadius: '8px' }}>
              {targets.map((t) => (
                <label key={t.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', fontSize: 13 }}>
                  <Checkbox size="small" checked={checked.has(t.key)} onChange={() => toggle(t.key)} disabled={running} inputProps={{ 'aria-label': t.name }} />
                  <span style={{ flex: 1, fontWeight: 700 }}>{t.name}</span>
                  <span style={{ fontSize: 11, color: t.kind === 'resv' ? '#0e7490' : '#16a34a' }}>{t.kind === 'resv' ? '💬予約チャット' : 'LINE'}</span>
                  <span style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>返却 {fmt(t.lastReturnDate)}</span>
                </label>
              ))}
            </Box>
            <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
              <Button onClick={onClose} size="small" disabled={running} sx={{ fontWeight: 700 }}>キャンセル</Button>
              <Button variant="contained" size="small" onClick={handleYes} disabled={running || count === 0} sx={{ fontWeight: 700 }}>
                {running ? '外しています…' : `はい（${count} 件のピンを外す）`}
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UnpinPastReturnsDialog;
